import React, { forwardRef, useState } from 'react'
import styled from 'styled-components'
import { Controller } from 'react-hook-form'
import { TextField, InputAdornment, IconButton } from '@mui/material'
import { AutoCompleteType, IFormError, InputRules } from '../../types/FormTypes'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { themeColors } from '../../const/colors'
import { faEyeSlash, faEye } from '@fortawesome/free-solid-svg-icons'

const Input: React.FC<{
  label: string
  name: string
  control: any
  errors: IFormError | any
  rules?: InputRules | any
  defaultValue?: string
  message?: string
  autoCompleteType?: AutoCompleteType
  InputProps?: any
  customErrorMessage?: boolean
  placeholder?: string
  [k: string]: any
}> = forwardRef(
  (
    {
      label,
      name,
      control,
      errors,
      rules,
      defaultValue = '',
      message = '',
      autoCompleteType,
      children,
      InputProps,
      customErrorMessage = false,
      placeholder,
      ...rest
    },
    ref?: React.Ref<HTMLInputElement>,
  ) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)
    const isPasswordInput = autoCompleteType === 'password'

    const handleClickShowPassword = () => {
      setIsPasswordVisible(!isPasswordVisible)
    }
    const handleMouseDownPassword = (event: any) => {
      event.preventDefault()
    }

    return (
      <>
        <Controller
          control={control}
          name={name}
          rules={rules}
          defaultValue={defaultValue}
          render={({ field: { onChange, onBlur, value } }) => (
            <InputContainer
              $isSpacedBelow={
                !(
                  message.length ||
                  (errors[name]?.message && !customErrorMessage)
                )
              }
            >
              <InputCustom
                ref={ref}
                variant="outlined"
                label={label}
                value={value}
                type={
                  !isPasswordVisible && isPasswordInput ? 'password' : 'text'
                }
                onChange={onChange}
                onWheel={(e: any) => e.target.blur()}
                onBlur={onBlur}
                error={!!errors[name]}
                autoComplete={autoCompleteType}
                color={'primary'}
                placeholder={placeholder}
                fullWidth={true}
                {...rest}
                InputProps={
                  isPasswordInput
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              onMouseDown={handleMouseDownPassword}
                              edge="end"
                              size="large"
                            >
                              {isPasswordVisible ? (
                                <FontAwesomeIcon icon={faEye} size="sm" />
                              ) : (
                                <FontAwesomeIcon icon={faEyeSlash} size="sm" />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }
                    : InputProps
                    ? InputProps
                    : undefined
                }
                {...children}
              />
            </InputContainer>
          )}
        />
        {!customErrorMessage && errors[name]?.message && (
          <InputMessage color={themeColors.error}>
            {errors[name].message}
          </InputMessage>
        )}

        {!!message.length && !errors[name]?.message && (
          <InputMessage color="rgba(0,0,0,0.6)">{message}</InputMessage>
        )}
      </>
    )
  },
)
const InputCustom = styled(TextField)`
  background-color: ${themeColors.white};
  max-width: 500px;
`
const InputContainer = styled.div<{ $isSpacedBelow: boolean }>`
  ${props => props.$isSpacedBelow && 'margin-bottom: 16px;'}
`
const InputMessage = styled.p<{ color: string }>`
  color: ${props => props.color};
  font-size: 14px;
  margin: 0px;
  padding-left: 16px;
  padding-top: 5px;
  padding-bottom: 9px;
`

export default Input
